import { NpcBenchesPanel } from '@/components/admin/npc/npc-benches-panel';
import { NpcAvatar } from '@/components/admin/npc/npc-card';
import { NpcDeleteDialog } from '@/components/admin/npc/npc-delete-dialog';
import { NpcFields } from '@/components/admin/npc/npc-fields';
import { NpcOriginNote } from '@/components/admin/npc/npc-origin';
import { NpcReplaysTable } from '@/components/admin/npc/npc-replays-table';
import { NpcTabStrip } from '@/components/admin/npc/npc-tab-strip';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { useAdminSession } from '@/lib/admin-session';
import { deleteNpc, fetchNpcReferents, fetchNpcReplays, npcsApi } from '@/lib/api';
import {
  NPC_DRAFT_KIT,
  type NpcDraft,
  npcDraftFromContent,
  openingNpcDraft,
} from '@/lib/npc-draft';
import { npcRefusalSentence, sourcesWord, versionPlayedSentence } from '@/lib/npc-labels';
import type { NpcFicheTab } from '@/lib/npc-search';
import { NPC_WORDING } from '@/lib/npc-wording';
import { useReplaySettings } from '@/lib/use-replay-settings';
import type {
  NpcContent,
  NpcDetail,
  NpcDraftContent,
  NpcPrefill,
  NpcVersion,
  VersionSummary,
} from '@matter_extractor/shared';
import { NPC_KIND_LABELS } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';

/**
 * La fiche d'un NPC : le patron de §4, plus le formulaire de §3.1 (npc.md, issue #210).
 *
 * Everything this screen does that the persona's editor does identically - the head, the history,
 * the retirement, the boxes, the debounced autosave and its optimistic control, the rename on blur -
 * is `VersionedEditor`. What is left here is what only an NPC knows: its API, its draft kit, its
 * wording, its form, and what the layer's four NPC openings carry:
 *
 *   - **`belowHeader` and `bodyInstead`** - the tab strip « Fiche · Rejeux 12 » (M3) and, when the
 *     second tab is open, the table of §10 in place of the form. The editor STAYS mounted behind
 *     it: switching to the replays and back loses neither the draft nor its autosave;
 *   - **`usageInstead`** - « Dans les bancs » in place of « Utilisé dans »: nothing cites an NPC;
 *   - **`asideTop`** - « Lancer un rejeu » and « Regarder le dernier rejeu »
 *     (§6, §11) hang above the history: the route passes `NpcFicheAside`;
 *   - **`asideBottom`** - the quiet red « Supprimer définitivement », under the retirement, and the
 *     box it opens.
 *
 * ## One side, no production
 *
 * `NPC_WORDING.developmentOnly` removes every passage to production the layer draws - the hover
 * icon of the history, the head's button, the production pill - and `refuseProduction` refuses it
 * on the server. « Jouée par N rejeux » replaces « utilisé dans » on each line of the history
 * (§3.4).
 *
 * ## The prefill of a session (§4.2)
 *
 * With a `prefill` and no `detail`, the editor opens on the draft the session gave - the same
 * kit with another `opening` - and writes nothing until the first « Enregistrer », which is the v1.
 * `onCreated` navigates to the new fiche, the same hand-off as the empty one.
 */

export interface NpcEditorProps {
  /** The entity, or null for an NPC that does not exist yet. */
  detail: NpcDetail | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: NpcVersion | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  onReload: () => void;
  selectName?: boolean;
  /** The draft of « Créer un NPC » (§4.2): opens a NEW fiche on it. Ignored with a `detail`. */
  prefill?: NpcPrefill | null;
  /** Which tab of the fiche is open (§10). Meaningless on a new NPC, which has no replays. */
  tab?: NpcFicheTab;
  /** Hung above the history in the right column: the launch box and « Regarder » (`NpcFicheAside`). */
  asideTop?: ReactNode;
  /** Drawn between the breadcrumb and the head: the banner of a prefilled draft. */
  banner?: ReactNode;
}

export function NpcEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
  selectName = false,
  prefill = null,
  tab = 'fiche',
  asideTop,
  banner,
}: NpcEditorProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = useReplaySettings();
  const [deleting, setDeleting] = useState(false);

  // A kit per prefill and not per render: the layer reads it at mount to seed its draft.
  const kit = useMemo(
    () =>
      prefill === null
        ? NPC_DRAFT_KIT
        : {
            ...NPC_DRAFT_KIT,
            opening: (opened: Parameters<typeof openingNpcDraft>[0]): NpcDraft =>
              opened === null
                ? npcDraftFromContent({
                    kind: 'session',
                    ...prefill.content,
                    originSessionId: prefill.content.originSessionId ?? prefill.sessionId,
                  })
                : openingNpcDraft(opened),
          },
    [prefill],
  );

  const id = detail?.id ?? null;
  const referents = useQuery({
    queryKey: ['npc-referents', email, id],
    queryFn: () => fetchNpcReferents(id ?? ''),
    enabled: id !== null,
    retry: false,
  });
  const replays = useQuery({
    queryKey: ['npc-replays', email, id],
    queryFn: () => fetchNpcReplays(id ?? ''),
    enabled: id !== null && tab === 'rejeux',
    retry: false,
  });

  const removal = useMutation({
    mutationFn: () => deleteNpc(id ?? ''),
    onSuccess: () => {
      setDeleting(false);
      void queryClient.invalidateQueries({ queryKey: ['npcs'] });
      void queryClient.invalidateQueries({ queryKey: ['npc-benches'] });
      void queryClient.invalidateQueries({ queryKey: ['npc-series'] });
      queryClient.removeQueries({ queryKey: ['npc', email, id] });
      void navigate({ to: '/admin/npc' });
    },
  });

  const versionCounts = new Map(
    (detail?.versionReplayCounts ?? []).map((entry) => [entry.versionId, entry.count]),
  );

  return (
    <VersionedEditor<NpcContent, NpcDraftContent, NpcDraft>
      wording={NPC_WORDING}
      api={npcsApi}
      kit={kit}
      keys={{ detail: 'npc', list: 'npcs' }}
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={(npcId) => void navigate({ to: '/admin/npc/$npcId', params: { npcId } })}
      selectName={selectName}
      ownRefusal={npcRefusalSentence}
      breadcrumb={banner ?? null}
      heading={({ draft }) => ({
        title: (
          <span className="inline-flex items-center gap-3">
            <NpcAvatar firstName={draft.firstName} kind={draft.kind} size="lg" />
            <span className="min-w-0">
              {draft.name.trim() === '' ? NPC_WORDING.newTitle : draft.name}
            </span>
          </span>
        ),
        subtitle: `${NPC_KIND_LABELS[draft.kind]} · ${
          draft.firstName.trim() === '' ? 'prénom à saisir' : draft.firstName.trim()
        } · ${sourcesWord(draft.sources.length)}`,
      })}
      belowHeader={
        detail === null ? null : (
          <NpcTabStrip
            label="Fiche du NPC"
            data-testid="npc-fiche-onglets"
            active={tab}
            tabs={[
              { id: 'fiche', label: 'Fiche' },
              { id: 'rejeux', label: 'Rejeux', count: detail.replayCount },
            ]}
            hrefOf={(entry) =>
              entry.id === 'rejeux'
                ? `/admin/npc/${detail.id}?onglet=rejeux`
                : `/admin/npc/${detail.id}`
            }
            renderTab={(entry, props) => (
              <Link
                to="/admin/npc/$npcId"
                params={{ npcId: detail.id }}
                search={entry.id === 'rejeux' ? { onglet: 'rejeux' } : {}}
                activeOptions={{ exact: true }}
                className={props.className}
              >
                {props.children}
              </Link>
            )}
          />
        )
      }
      {...(detail !== null && tab === 'rejeux'
        ? {
            bodyInstead: (
              <div data-testid="npc-onglet-rejeux">
                <NpcReplaysTable
                  replays={replays.isError ? null : (replays.data ?? undefined)}
                  current={{
                    versionNumber: detail.development.number,
                    expected: detail.developmentContent.expectedNoyau,
                  }}
                />
              </div>
            ),
          }
        : {})}
      renderFields={(binding) => (
        <NpcFields
          draft={binding.draft}
          readOnly={binding.readOnly}
          nameError={binding.nameError}
          settings={settings.isError ? null : settings.data}
          originNote={
            binding.draft.originSessionId === null ? null : (
              <NpcOriginNote sessionId={binding.draft.originSessionId} />
            )
          }
          onDraft={binding.onDraft}
          onNameCommit={binding.onNameCommit}
        />
      )}
      {...(asideTop === undefined ? {} : { asideTop })}
      usageInstead={
        detail === null ? null : (
          <NpcBenchesPanel referents={referents.isError ? null : referents.data} />
        )
      }
      renderHistoryLineExtra={(version) => {
        const count = versionCounts.get(version.id) ?? 0;
        return count === 0 ? null : (
          <p data-testid="npc-version-jouee" className="text-caption text-gray-600 leading-4">
            {versionPlayedSentence(count)}
          </p>
        );
      }}
      asideBottom={
        detail === null ? null : (
          <div className="space-y-1.5 px-1">
            <button
              type="button"
              data-testid="npc-supprimer"
              onClick={() => {
                removal.reset();
                setDeleting(true);
              }}
              className="cursor-pointer font-semibold text-rouge-deep text-small underline underline-offset-2 hover:text-rouge focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              Supprimer définitivement
            </button>
            <p className="text-caption text-gray-600 leading-4">
              Efface le NPC et toutes ses versions, et met ses rejeux à la corbeille. Pour une
              demande d’effacement d’une personne qui a donné un NPC.
            </p>
            {deleting ? (
              <NpcDeleteDialog
                name={detail.name}
                versionCount={detail.versions.length}
                replayCount={detail.replayCount}
                benches={referents.data?.benches ?? null}
                busy={removal.isPending}
                error={
                  removal.isError
                    ? (npcRefusalSentence(removal.error) ??
                      'La suppression a échoué. Réessayez dans un instant.')
                    : null
                }
                onConfirm={() => removal.mutate()}
                onCancel={() => setDeleting(false)}
              />
            ) : null}
          </div>
        )
      }
    />
  );
}
